import { HttpErrorResponse } from '@angular/common/http';
import { get } from 'lodash';
import { IBriefAnalyzeErrorMessage } from './brief.interface';

interface IBriefErrorPattern {
  key: string;
  pattern: RegExp;
}

const GENERIC_ERROR_KEY = 'dialogs.createCampaignDialog.briefAnalyzeError';
const ERROR_KEY_PREFIX = 'dialogs.createCampaignDialog.briefAnalyzeErrors';
const BAD_REQUEST_ERROR_CODE = 'ERR_1002';

const FILE_ERROR_KEYS_BY_CODE: Record<string, string> = {
  ERR_5005: 'invalidFileContent',
  ERR_5026: 'invalidFileName',
  ERR_5030: 'malwareDetected',
  ERR_5031: 'malwareScanFailed',
};

// Matched against the English sentences of CampaignManagementAPI's brief service
// (app/services/brief/). A reworded sentence silently falls back to the generic message.
const FILE_ERROR_PATTERNS: IBriefErrorPattern[] = [
  { key: 'emptyFile', pattern: /is empty -- there is no text in it to read/ },
  { key: 'noReadableText', pattern: /contains no readable text/ },
  { key: 'unreadableFile', pattern: /could not be read\. The file may be damaged/ },
  { key: 'notUtf8', pattern: /is not UTF-8 text/ },
];

const REQUEST_ERROR_PATTERNS: IBriefErrorPattern[] = [
  { key: 'budgetExhausted', pattern: /AI budget for your account has been used up/ },
  { key: 'aiUnavailable', pattern: /AI service could not be reached/ },
];

function findPatternKey(patterns: IBriefErrorPattern[], message: string): string | undefined {
  return patterns.find(({ pattern }) => pattern.test(message))?.key;
}

// Every /brief/analyze failure arrives with `details: []`, so ErrorFormatterService has nothing to
// translate. File checks carry their own error codes; everything else shares BAD_REQUEST and is
// told apart only by its sentence. The file name is taken from the uploads the backend quotes, not
// parsed out of the message.
export function getBriefAnalyzeErrorMessage(error: HttpErrorResponse, fileNames: string[]): IBriefAnalyzeErrorMessage {
  const errorCode: string = get(error.error, 'error_code', '') ?? '';
  const message: string = get(error.error, 'message', '') ?? '';
  const isBadRequest = errorCode === BAD_REQUEST_ERROR_CODE;
  const fileName = fileNames.find((name) => message.includes(`'${name}'`));
  const fileErrorKey =
    FILE_ERROR_KEYS_BY_CODE[errorCode] ?? (isBadRequest ? findPatternKey(FILE_ERROR_PATTERNS, message) : undefined);

  if (fileErrorKey && fileName) {
    return { params: { fileName }, translationKey: `${ERROR_KEY_PREFIX}.${fileErrorKey}` };
  }

  const requestErrorKey = isBadRequest ? findPatternKey(REQUEST_ERROR_PATTERNS, message) : undefined;

  if (requestErrorKey) {
    return { params: {}, translationKey: `${ERROR_KEY_PREFIX}.${requestErrorKey}` };
  }

  return { params: {}, translationKey: GENERIC_ERROR_KEY };
}
